import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, Undo2 } from 'lucide-react';
import {
    createMileageExpense,
    updateMileageExpense,
    deleteExpenseV2,
    createBulkExpense,
} from '../lib/api';
import { hapticSave } from '../lib/haptics';
import MileageRecentEntries from './mileage-recent-entries';

// ── Constants mirrored from backend/config/expense_config.py ──────────────────
const VEHICLE_TYPES = [
    { id: 'own_car', name: 'Own Car' },
    { id: 'company_car', name: 'Company Car' },
    { id: 'motorcycle', name: 'Motorcycle' },
    { id: 'electric_car', name: 'Electric Car' },
    { id: 'other', name: 'Other' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Seed a row from an existing expense doc. Normalizes missing optional fields. */
function seedRowFromExpense(exp) {
    return {
        id: exp.id,
        from_location: exp.from_location || '',
        to_location: exp.to_location || '',
        distance_mil: exp.distance_mil != null ? String(exp.distance_mil) : '',
        vehicle_type: exp.vehicle_type || 'own_car',
        round_trip: exp.round_trip ?? false,
        project_id: exp.project_id || '',
        // Phase 23 (PRD 12.1): activity_id is the join key, activity_name the
        // snapshot resolved server-side — never re-derived from it on read.
        activity_id: exp.activity_id || '',
        activity_name: exp.activity_name || '',
        billable: exp.billable ?? false,
        notes: exp.notes || '',
        payment_method: exp.payment_method || 'personal_card',
        _markDeleted: false,
    };
}

/** Fresh empty add-row (no id). */
function emptyRow() {
    return {
        id: undefined,
        from_location: '',
        to_location: '',
        distance_mil: '',
        vehicle_type: 'own_car',
        round_trip: false,
        project_id: '',
        activity_id: '',
        activity_name: '',
        billable: false,
        notes: '',
        payment_method: 'personal_card',
        _markDeleted: false,
    };
}

/**
 * Compare two row objects for meaningful field changes.
 * Ignores internal bookkeeping fields.
 */
function hasChanged(current, original) {
    const fields = [
        'from_location', 'to_location', 'distance_mil',
        'vehicle_type', 'round_trip', 'project_id', 'activity_id',
        'billable', 'notes', 'payment_method',
    ];
    return fields.some((f) => String(current[f] ?? '') !== String(original[f] ?? ''));
}

/**
 * Build the minimal payload for updateMileageExpense.
 * MileageExpenseUpdate is fully optional — only send what may have changed.
 *
 * `original` is the row's initialSnapshot entry (undefined only if it's
 * somehow missing, treated as "changed" — the safe default).
 *
 * 23.R4: activity_id is included ONLY when it actually changed from the
 * snapshot. The backend re-validates the activity whenever this key is
 * present in the payload at all — so unconditionally sending
 * `row.activity_id || null` (the previous behaviour) re-triggered that
 * validation on every save of a row that HAS an activity, even when the
 * edit touched an unrelated field like distance or notes. If that
 * activity had since been deleted from the project, the unrelated edit
 * was rejected — exactly the case KD-083 / finding 5.175 (Principle 2,
 * "No Data Loss, Ever") was written to protect. Omitting the key when
 * unchanged keeps a legacy/edited row editable; when the user does
 * change (or clear) the activity, row.activity_id already differs from
 * original.activity_id and gets sent.
 */
function extractUpdatablePayload(row, original) {
    const payload = {
        from_location: row.from_location,
        to_location: row.to_location,
        distance_mil: parseFloat(row.distance_mil) || 0,
        vehicle_type: row.vehicle_type,
        round_trip: row.round_trip,
        project_id: row.project_id || null,
        billable: row.billable,
        notes: row.notes || null,
        payment_method: row.payment_method,
    };
    const activityChanged = !original || String(row.activity_id ?? '') !== String(original.activity_id ?? '');
    if (activityChanged) {
        payload.activity_id = row.activity_id || null;
    }
    return payload;
}

/**
 * DayMileageForm
 *
 * Multi-entry mileage form for a single date. Shows existing mileage entries
 * for the day, allows inline editing, soft-delete, and adding new rows.
 * One Save button batches creates / updates / deletes via Promise.allSettled.
 *
 * In bulk mode (`dates` provided), `existingMileage` is always empty (parent
 * passes []), so there's naturally just one blank add-row; Save creates it
 * for every date in `dates` via the shared POST /expenses/bulk endpoint
 * (mileage has no date-relative fields, so one bulk call is safe/simpler
 * than N individual creates).
 *
 * Props:
 *   selectedDay     {string}    — "YYYY-MM-DD"; single-day mode
 *   dates           {string[]}  — ISO dates; bulk mode
 *   existingMileage {Array}     — expense docs (category: mileage) for selectedDay
 *   projects        {Array}     — active project list [{id, name}]
 *   onSaved         {function}  — () => void called after successful batch save
 *   onDirtyChange   {function}  — (dirty: bool) => void
 */
const DayMileageForm = forwardRef(function DayMileageForm({
    selectedDay,
    dates,
    existingMileage = [],
    projects = [],
    onSaved,
    onDirtyChange,
}, ref) {
    const { t } = useTranslation();
    const isBulkMode = Array.isArray(dates) && dates.length > 0;
    // ── State ──────────────────────────────────────────────────────────────────
    const [rows, setRows] = React.useState(() => {
        const existing = existingMileage.map(seedRowFromExpense);
        // When there are existing entries, show only those + "Add another" button.
        // When the day is empty, show one blank add-row (fast path for new-day use).
        return existing.length > 0 ? existing : [emptyRow()];
    });
    const [saving, setSaving] = React.useState(false);

    // Retry set for bulk mode — starts as the full selection, narrows to only
    // the failed dates after a partial failure so a retry doesn't resubmit
    // (duplicate) the dates that already succeeded. Reset whenever the
    // parent hands us a new selection.
    const [bulkDates, setBulkDates] = React.useState(dates || []);
    React.useEffect(() => {
        if (isBulkMode) setBulkDates(dates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dates]);

    // Snapshot of initial state — keyed by id for existing rows
    const initialSnapshot = useRef(
        existingMileage.reduce((acc, exp) => {
            acc[exp.id] = seedRowFromExpense(exp);
            return acc;
        }, {})
    );

    // ── Re-seed when existingMileage prop changes (dialog reopened for new date) ──
    React.useEffect(() => {
        const seeded = existingMileage.map(seedRowFromExpense);
        setRows(seeded.length > 0 ? seeded : [emptyRow()]);
        initialSnapshot.current = existingMileage.reduce((acc, exp) => {
            acc[exp.id] = seedRowFromExpense(exp);
            return acc;
        }, {});
        onDirtyChange?.(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDay, existingMileage]);

    // ── Dirty computation ─────────────────────────────────────────────────────
    const isDirty = React.useMemo(() => {
        for (const row of rows) {
            const isNew = !row.id;
            if (isNew && !row._markDeleted) {
                // New row is dirty if any field has a value
                if (row.from_location || row.to_location || row.distance_mil) return true;
            } else if (!isNew) {
                if (row._markDeleted) return true; // pending delete
                const orig = initialSnapshot.current[row.id];
                if (orig && hasChanged(row, orig)) return true;
            }
        }
        return false;
    }, [rows]);

    // Notify parent of dirty state changes
    React.useEffect(() => {
        onDirtyChange?.(isDirty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDirty]);

    // ── Row mutation helpers ───────────────────────────────────────────────────
    const updateRowField = (rowIndex, field, value) => {
        setRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, [field]: value } : r));
    };

    const markRowDeleted = (rowIndex) => {
        setRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, _markDeleted: true } : r));
    };

    const undoRowDelete = (rowIndex) => {
        setRows(prev => prev.map((r, i) => i === rowIndex ? { ...r, _markDeleted: false } : r));
    };

    const addRow = () => {
        setRows(prev => [...prev, emptyRow()]);
    };

    // ── Recent-entry auto-fill ─────────────────────────────────────────────────
    const handleRecentSelect = (entry) => {
        setRows(prev => {
            // Find the last blank (new, non-deleted) row
            const lastBlankIdx = prev.reduce((found, r, i) => {
                if (!r.id && !r._markDeleted) return i;
                return found;
            }, -1);

            // Phase 23 (PRD 12.2, KD-083 "consequence accepted at approval"):
            // GET /expenses/mileage/recent never returns project_id (routes
            // dedupe purely on from/to location, deliberately excluding
            // project — the same route can legitimately serve different
            // projects), so there is no project on the reused entry to
            // resolve an activity against. `fill` intentionally leaves
            // project_id/activity_id/activity_name untouched: the row keeps
            // whatever the user already had selected, and if that project
            // defines activities the now-visible required field (and the
            // validation below) asks for one before save — exactly the
            // accepted behaviour, never a silently-carried stale activity.
            const fill = {
                from_location: entry.from_location || '',
                to_location: entry.to_location || '',
                distance_mil: entry.distance_mil != null ? String(entry.distance_mil) : '',
                vehicle_type: entry.vehicle_type || 'own_car',
                round_trip: entry.round_trip ?? false,
            };

            if (lastBlankIdx >= 0) {
                return prev.map((r, i) => i === lastBlankIdx ? { ...r, ...fill } : r);
            }
            // No blank row — append one and fill
            return [...prev, { ...emptyRow(), ...fill }];
        });
    };

    // ── Save handler ───────────────────────────────────────────────────────────
    const handleSave = async () => {
        // Project is mandatory on every new mileage entry (backend requires
        // it — a missing project_id 422s) — check every row that actually
        // has data before touching the network.
        for (const row of rows) {
            // A row being deleted is never validated — an existing legacy row
            // with no project_id must still be deletable.
            if (row._markDeleted) continue;
            const isNew = !row.id;
            const hasData = row.from_location || row.to_location || row.distance_mil;
            if (isNew && !hasData) continue;
            // Only rows this save will actually send are validated: a new row
            // with data, or an existing row the user changed. An untouched
            // legacy row with no project_id isn't re-sent, so requiring one
            // would block saving the rest of the form for no reason.
            const original = initialSnapshot.current[row.id];
            const willBeSent = isNew || (original && hasChanged(row, original));
            if (willBeSent && !row.project_id) {
                toast.error(t('expenses.receipt.project_required'));
                return;
            }
            // Phase 23 (PRD 12.2, 23.R3): mirrors backend/utils/activities.py's
            // resolve_activity, which on an UPDATE only re-validates
            // activity_id when the row is new, its project changed, or the
            // caller is actively changing activity_id — an edit that touches
            // neither stays permissive server-side, so a legacy row with no
            // activity remains editable on unrelated fields (Principle 2 "No
            // Data Loss, Ever"; this is what keeps rows editable after an
            // activity is deleted from a project, finding 5.175, and it has
            // tests). Gating on willBeSent alone was stricter than the
            // server and forced an activity pick for e.g. a distance-only
            // correction on a legacy row.
            const projectChanged = !original || String(row.project_id ?? '') !== String(original.project_id ?? '');
            const activityChanged = !original || String(row.activity_id ?? '') !== String(original.activity_id ?? '');
            const activityMustBeValidated = isNew || projectChanged || activityChanged;
            if (activityMustBeValidated && row.project_id) {
                const selectedProject = projects?.find(p => p.id === row.project_id);
                const projectActivities = selectedProject?.activities || [];
                if (projectActivities.length > 0 && !row.activity_id) {
                    toast.error(t('error.activities.required'));
                    return;
                }
            }
        }

        setSaving(true);

        if (isBulkMode) {
            const row = rows[0];
            if (!row || (!row.from_location && !row.to_location && !row.distance_mil)) {
                setSaving(false);
                onDirtyChange?.(false);
                onSaved?.();
                return;
            }
            try {
                const res = await createBulkExpense(bulkDates, 'mileage', {
                    from_location: row.from_location,
                    to_location: row.to_location,
                    distance_mil: parseFloat(row.distance_mil) || 0,
                    vehicle_type: row.vehicle_type,
                    round_trip: row.round_trip,
                    project_id: row.project_id || null,
                    activity_id: row.activity_id || null,
                    billable: row.billable,
                    notes: row.notes || null,
                    payment_method: row.payment_method,
                });
                const failed = res.data?.failed || [];
                if (failed.length > 0) {
                    // Narrow the retry set to only the dates that actually
                    // failed — the ones that succeeded must not be resubmitted.
                    setBulkDates(failed.map(f => f.date));
                    toast.error(t('expenses.mileage.save_error'));
                    throw new Error('Mileage bulk save failed');
                }
                hapticSave();
                setRows([emptyRow()]); // fully saved — reset so a re-save can't duplicate
                onDirtyChange?.(false);
                onSaved?.();
            } finally {
                setSaving(false);
            }
            return;
        }

        const ops = [];
        const opLabels = [];

        for (const row of rows) {
            const isNew = !row.id;
            const isDeleted = row._markDeleted;

            // New row dropped before save — skip
            if (isNew && isDeleted) continue;

            // Pending delete of existing row
            if (!isNew && isDeleted) {
                ops.push(deleteExpenseV2(row.id));
                opLabels.push('delete');
                continue;
            }

            if (isNew) {
                // Skip entirely empty new rows
                if (!row.from_location && !row.to_location && !row.distance_mil) continue;
                ops.push(createMileageExpense({
                    date: selectedDay,
                    from_location: row.from_location,
                    to_location: row.to_location,
                    distance_mil: parseFloat(row.distance_mil) || 0,
                    vehicle_type: row.vehicle_type,
                    round_trip: row.round_trip,
                    project_id: row.project_id || null,
                    activity_id: row.activity_id || null,
                    billable: row.billable,
                    notes: row.notes || null,
                    payment_method: row.payment_method,
                }));
                opLabels.push('create');
                continue;
            }

            // Existing row — only update if changed
            const original = initialSnapshot.current[row.id];
            if (original && hasChanged(row, original)) {
                ops.push(updateMileageExpense(row.id, extractUpdatablePayload(row, original)));
                opLabels.push('update');
            }
        }

        if (ops.length === 0) {
            // Nothing to do — treat as clean
            onDirtyChange?.(false);
            onSaved?.();
            setSaving(false);
            return;
        }

        const results = await Promise.allSettled(ops);
        const failures = results.filter(r => r.status === 'rejected');

        if (failures.length === 0) {
            hapticSave();
            onDirtyChange?.(false);
            onSaved?.();
        } else {
            toast.error(t('expenses.mileage.save_error'));
            setSaving(false);
            throw new Error('Mileage save failed');
        }

        setSaving(false);
    };

    // Expose save function to parent via ref (used by Save Day orchestrator)
    useImperativeHandle(ref, () => ({ save: handleSave }));

    // ── Render ─────────────────────────────────────────────────────────────────
    const visibleRows = rows.map((row, index) => ({ row, index }));

    return (
        <div className="space-y-4 py-2">
            {/* Recent routes strip */}
            <MileageRecentEntries onSelect={handleRecentSelect} />

            {/* Rows */}
            {visibleRows.map(({ row, index }) => {
                const isExisting = !!row.id;
                const isDeleted = row._markDeleted;

                return (
                    <div
                        key={row.id ?? `new-${index}`}
                        className={`border border-border p-4 space-y-4 transition-opacity ${isDeleted ? 'opacity-40' : ''}`}
                    >
                        {/* Row header */}
                        <div className="flex justify-between items-center">
                            <span className={`text-xs uppercase tracking-widest text-muted-foreground ${isDeleted ? 'line-through' : ''}`}>
                                {isExisting ? t('expenses.mileage.entry_label', { num: index + 1 }) : t('expenses.mileage.new_entry')}
                            </span>
                            <div className="flex items-center gap-1">
                                {isExisting && !isDeleted && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => markRowDeleted(index)}
                                        className="min-h-[44px] min-w-[44px] p-0 text-destructive"
                                        disabled={saving}
                                        aria-label={t('common.delete')}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                )}
                                {isExisting && isDeleted && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => undoRowDelete(index)}
                                        className="min-h-[44px] min-w-[44px] p-0"
                                        disabled={saving}
                                        aria-label={t('common.undo')}
                                    >
                                        <Undo2 className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                        </div>

                        {/* Field grid — disabled when deleted or saving */}
                        <fieldset disabled={isDeleted || saving} className="space-y-4 border-0 p-0 m-0">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="label-uppercase mb-2 block">{t('expenses.mileage.from_label')}</Label>
                                    <Input
                                        value={row.from_location}
                                        onChange={(e) => updateRowField(index, 'from_location', e.target.value)}
                                        placeholder={t('expenses.mileage.from_label')}
                                    />
                                </div>
                                <div>
                                    <Label className="label-uppercase mb-2 block">{t('expenses.mileage.to_label')}</Label>
                                    <Input
                                        value={row.to_location}
                                        onChange={(e) => updateRowField(index, 'to_location', e.target.value)}
                                        placeholder={t('expenses.mileage.to_label')}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="label-uppercase mb-2 block">{t('expenses.mileage.distance_label')}</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={row.distance_mil}
                                        onChange={(e) => updateRowField(index, 'distance_mil', e.target.value)}
                                        placeholder="0.0"
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">1 mil = 10 km</p>
                                </div>
                                <div>
                                    <Label className="label-uppercase mb-2 block">{t('expenses.mileage.vehicle_label')}</Label>
                                    <select
                                        value={row.vehicle_type}
                                        onChange={(e) => updateRowField(index, 'vehicle_type', e.target.value)}
                                        className="w-full h-10 px-3 border border-input bg-background text-sm"
                                    >
                                        {VEHICLE_TYPES.map(v => (
                                            <option key={v.id} value={v.id}>{v.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id={`round-trip-${index}`}
                                    checked={row.round_trip}
                                    onCheckedChange={(checked) => updateRowField(index, 'round_trip', !!checked)}
                                />
                                <Label htmlFor={`round-trip-${index}`} className="text-sm cursor-pointer">
                                    {t('expenses.mileage.round_trip')}
                                </Label>
                            </div>

                            <div>
                                <Label className="label-uppercase mb-2 block">
                                    {t('expenses.receipt.project_label')} <span className="text-destructive">*</span>
                                </Label>
                                {projects.length > 0 ? (
                                    <select
                                        value={row.project_id}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            // Phase 23 (PRD 12.2): changing the project re-evaluates
                                            // whether an activity applies at all — clear a
                                            // now-invalid selection rather than silently carry an
                                            // activity id that belongs to the old project.
                                            setRows(prev => prev.map((r, i) => i === index
                                                ? { ...r, project_id: val, activity_id: '', activity_name: '' }
                                                : r));
                                        }}
                                        className="w-full h-10 px-3 border border-input bg-background text-sm"
                                        data-testid={`project-select-${index}`}
                                    >
                                        <option value="">{t('expenses.receipt.project_placeholder')}</option>
                                        {projects.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <p className="text-xs text-destructive">{t('expenses.receipt.no_projects_available')}</p>
                                )}
                            </div>

                            {row.project_id && (() => {
                                const selectedProject = projects?.find(p => p.id === row.project_id);
                                const projectActivities = selectedProject?.activities || [];
                                if (projectActivities.length === 0) return null;
                                // Phase 23 (PRD 12.2): shown ONLY when the project defines
                                // activities — a project with none renders no field at all
                                // (matches day-time-entries-form.jsx's pattern exactly).
                                return (
                                    <div>
                                        <Label className="label-uppercase mb-2 block">
                                            {t('timesheet.bulk_dialog.activity_label')} <span className="text-destructive">*</span>
                                        </Label>
                                        <select
                                            value={row.activity_id || ''}
                                            onChange={(e) => {
                                                const actId = e.target.value;
                                                const act = projectActivities.find(a => a.id === actId);
                                                setRows(prev => prev.map((r, i) => i === index
                                                    ? { ...r, activity_id: actId, activity_name: act ? act.name : '' }
                                                    : r));
                                            }}
                                            className="w-full h-10 px-3 border border-input bg-background text-sm"
                                            data-testid={`activity-select-${index}`}
                                            aria-required="true"
                                        >
                                            <option value="">{t('timesheet.bulk_dialog.activity_placeholder')}</option>
                                            {projectActivities.map(act => (
                                                <option key={act.id} value={act.id}>{act.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                );
                            })()}

                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id={`billable-${index}`}
                                    checked={row.billable}
                                    onCheckedChange={(checked) => updateRowField(index, 'billable', !!checked)}
                                />
                                <Label htmlFor={`billable-${index}`} className="text-sm cursor-pointer">
                                    {t('expenses.receipt.billable_label')}
                                </Label>
                            </div>

                            <div>
                                <Label className="label-uppercase mb-2 block">{t('expenses.receipt.notes_label')}</Label>
                                <Input
                                    value={row.notes}
                                    onChange={(e) => updateRowField(index, 'notes', e.target.value)}
                                    placeholder={t('expenses.receipt.notes_placeholder')}
                                />
                            </div>
                        </fieldset>

                        {isDeleted && (
                            <p className="text-xs text-muted-foreground">
                                {t('expenses.receipt.delete_pending_notice')}{' '}
                                <button
                                    type="button"
                                    onClick={() => undoRowDelete(index)}
                                    className="underline hover:no-underline inline-flex items-center min-h-[44px] px-1"
                                    disabled={saving}
                                >
                                    {t('common.undo')}
                                </button>
                            </p>
                        )}
                    </div>
                );
            })}

            {/* Add another row — hidden in bulk mode: a bulk save only ever
                submits rows[0] (the one entry applied to every selected
                date), so a second row would silently be discarded. */}
            {!isBulkMode && (
                <Button
                    variant="outline"
                    className="w-full uppercase tracking-wider text-xs"
                    onClick={addRow}
                    disabled={saving}
                >
                    <Plus className="w-4 h-4 mr-2" />
                    {t('expenses.mileage.add_another')}
                </Button>
            )}

        </div>
    );
});

export default DayMileageForm;
